// PathSceneryFeature — Set13 (path-driven scenery placement). DB-free data class: lives on
// `ObjPath.sceneryFeature` (a sibling of `terrainFeature` / `materialFeature`), flows through the
// `PackageUpMapObjForTerrain` whitelist, and is consumed by `collectSceneryRegions`
// (Terrain/Scenery/Placement/SceneryRegions.ts). "DB-free" = no mobx-graphlink / web-vcore deps,
// so the Node test lane can import it directly.
import type {MaterialMask} from "../Materials/@MaterialFeature.js";

/** One weighted contribution to a mixed-kind region (Set16). */
export class SceneryKindEntry {
	constructor(data?: Partial<SceneryKindEntry>) { Object.assign(this, data); }
	/** Which scenery kind (catalogue id) this path places. */
	kindID = "";
	/** Selection weight — controls the MIX (the ratio of placed counts across kinds). Absent/1 =
	 *  equal. <= 0 is treated as "never existed" and dropped by NormalizeKinds.
	 *  Relative (NOT normalised at author time): the sum need not be 1. */
	weight: number = 1;
}

/** Set13 — a path's scenery-placement feature (`ObjPath.sceneryFeature`, sibling of
 *  `terrainFeature` / `materialFeature`). Where the path's geometry becomes a SCENERY REGION: the
 *  same "fill" / "stroke" / "fillAndStroke" vocabulary as `PathMaterialFeature`, but the region's
 *  job is to place discrete objects (trees, rocks, …) rather than paint ground colour.  The two
 *  are ORTHOGONAL by design (§4.10): scenery never implies a material change.
 *
 *  ASYMMETRY vs `PathMaterialFeature`: there is deliberately NO `baseSceneryKindID`/fallback field.
 *  Every terrain fragment must have some material, so materials need a map-wide fallback; scenery's
 *  default is NOTHING — an unpainted area has no objects on it.
 *
 *  A region is a PREDICATE (`SceneryRegion.geoFactorAt(p)`), NEVER a list of instances — a
 *  Texas-sized fill would be ~19 billion instances and enumerating them IS the crash (§3.4). */
export class PathSceneryFeature {
	constructor(data?: Partial<PathSceneryFeature>) { Object.assign(this, data); }

	/** Whether `sf` is "active": `!sf.disabled` AND (≥1 usable kind entry post-`NormalizeKinds` OR `suppressPriorScenery > 0` — a suppression-only entry). Null returns false. */
	static IsActive(sf: PathSceneryFeature | n, resolvable?: ReadonlySet<string>|n): sf is PathSceneryFeature {
		if (sf == null) return false;
		if (sf.disabled) return false;
		if (PathSceneryFeature.NormalizeKinds(sf, resolvable).length > 0) return true;
		return PathSceneryFeature.SuppressionOf(sf) > 0;
	}

	/** The resolved suppression fraction in [0, 1]: `suppressPriorScenery` clamped, with absent/NaN/negative → 0 (§8 footgun). */
	static SuppressionOf(sf: PathSceneryFeature | n): number {
		if (sf == null) return 0;
		const v = sf.suppressPriorScenery;
		if (typeof v != "number" || !Number.isFinite(v)) return 0;
		if (v <= 0) return 0;
		return Math.min(v, 1);
	}

	/** PURE kind-list normalisation (Set16 §1) — DB-free, so the Node test lane imports it directly.
	 *  Drops, in order: null entries, empty `kindID`, `weight <= 0` (review: treated as though they
	 *  never existed), and — when a `resolvable` catalogue set is supplied — ids not in it. Surviving
	 *  entries keep their RELATIVE weights unnormalised (the placer's `pickWeighted` handles
	 *  relative/unnormalised weights, Set16 §4). Absent weight → 1. */
	static NormalizeKinds(sf: PathSceneryFeature | n, resolvable?: ReadonlySet<string>|n): SceneryKindEntry[] {
		if (sf == null) return [];
		const out: SceneryKindEntry[] = [];
		for (const k of sf.kinds ?? []) {
			if (k == null) continue;
			const kindID = k.kindID ?? "";
			if (!kindID) continue;
			const weight = (typeof k.weight == "number" && Number.isFinite(k.weight)) ? k.weight : 1;
			if (!(weight > 0)) continue;
			if (resolvable != null && !resolvable.has(kindID)) continue;
			out.push(new SceneryKindEntry({kindID, weight}));
		}
		return out;
	}

	/** When true, the feature is preserved in the persisted data but ignored by every consumer
	 *  (bridge/placer treat it as inert). Mirrors `PathMaterialFeature.disabled`. */
	disabled?: boolean|n;
	/** The kinds this region distributes, each with an authored weight (Set16). Non-empty post-`NormalizeKinds` ⇒ active — as does `suppressPriorScenery > 0` alone. `kinds` absent and `[]` are IDENTICAL (both place nothing). */
	kinds: SceneryKindEntry[] = [];
	/** Same vocabulary as `PathMaterialFeature` — "fill"/"fillAndStroke" need a closed path. */
	mode: "fill" | "stroke" | "fillAndStroke" = "fill";
	/** FULL-width in metres (the total stroke thickness). The region extends `strokeWidth/2` on
	 *  EACH side of the polyline. Required for "stroke" / "fillAndStroke"; ignored for plain
	 *  "fill". */
	strokeWidth?: number|n;
	/** Metres over which density ramps to zero CENTERED ON the region boundary (a soft forest edge).
	 *  Straddles the boundary at `feather/2` behind it and `feather/2` beyond it, with the boundary
	 *  itself at the 50% fade point. Thus `feather` is only an edge-SOFINESS knob — it does not add
	 *  a one-sided placement apron, so changing it does not substantially change the total count. */
	feather = 5;
	/** An optional SEED PERTURBATION for THIS region's placement streams ONLY (Set17 §2: presence /
	 *  jitter / rank / the mixed-kind draw). Absent (0/null/undefined) = the streams seed purely
	 *  from the region's stable `path_id` (`regionSeed = hashString32("sceneryRegion:" + id)`), so
	 *  two identically-authored features with different ids place differently. A NON-ZERO value
	 *  EXCLUSIVELY re-seeds this region — it shifts the deterministic placement pattern (which cells
	 *  spawn, their jitter, variant, rotation, …) WITHOUT changing anything else: the region's
	 *  geometry/bbox, density (occupancy/spacing/presence), feather, kinds, weights, dominance,
	 *  suppression, and the per-instance RANK stream are all untouched. This is the "reroll just
	 *  this feature" knob — e.g. two side-by-side copies of a forest that would otherwise place
	 *  IDENTICALLY (mirrored, or two regions sharing an id) can be decorrelated by giving one a
	 *  different seed. Scope is STRICTLY this region: it never perturbs any other region's streams,
	 *  and the per-INSTANCE rank hash (§3.7) is excluded so zoom-out thinning is unaffected.
	 *  The full-permutation corner (a seed that reproduces another's exact layout) is possible but
	 *  astronomically unlikely for a 32-bit stream.
	 *
	 *  ⚠ KIND-DRAW ASYMMETRY (see {@link PathSceneryFeature.kindSeedOverride}): this seed perturbs
	 *  the mixed-kind draw ONLY when `kindSeedOverride` is ABSENT — if a `kindSeedOverride` is set,
	 *  it REPLACES this value for the kind draw specifically, so the two seeds become independent
	 *  (a "distribution" knob here vs a "which-kind" knob there). */
	seedOverride?: number|n;
	/** An OPTIONAL seed perturbing ONLY the mixed-kind draw (which kind owns each cell, Set16 §3);
	 *  presence/jitter/rank follow `seedOverride`. WHEN SET it REPLACES `seedOverride` for the kind
	 *  draw — re-roll WHICH KIND each spot gets while keeping a good distribution. Absent = follows seedOverride. */
	kindSeedOverride?: number|n;
	/** Per-region overrides of the kind's spacing/presence (the density knobs). */
	spacingOverride?: number|n;
	presenceOverride?: number|n;
	/** Explicit dominance (HIGHER wins — matches `PathMaterialFeature.dominance`). Absent => derived from scenery containment nesting (§3.4): depth among interior-bearing regions, or above all fills for strokes. Matters only for suppression. */
	dominance?: number|n;
	/** Cut-out fraction (0..1) of instances placed over this area by STRICTLY LOWER dominance regions. Null/0 = none; 1 = fully clear. Feathered at this region's boundary; works with NO kinds (suppression-only). */
	suppressPriorScenery?: number|n;
	/** Optional MASKS narrowing where instances may stand — SAME class as materials (§3.4). */
	masks?: MaterialMask[]|n;
}
